import { atom } from 'jotai';
import { createResourceAtom } from 'lib/atoms';
import request from 'lib/request';
import type {
  MatchReviewResponse,
  WellLifecycleResponse,
  WellOverviewResponse,
} from '../types';
import { buildSectionMutationAtom } from 'lib/atoms/section-mutation';

// ---------------------------------------------------------------------------
// Well section data layer.
//
// Mirrors `pages/documents/structuring/atoms/structured-parties.ts`:
//   • the Overview list lives in a *module-level* atom (not createResourceAtom)
//     so the shell nav, the overview table, and Match Review all share one
//     piece of state and any mutation can trigger a refetch that updates them;
//   • the Match Review detail uses the codebase `createResourceAtom` factory
//     (auto-fetches on mount, setter refetches);
//   • lifecycle actions are `mutationAtom`s taking a uniform { url, method,
//     data } arg — we use the low-level primitive (rather than
//     `createMutationAtom`) to avoid registering AFE-structuring payload keys
//     in the shared `api-payloads.ts` registry.
// ---------------------------------------------------------------------------

// ── Overview list (GET /documents/:documentId/afe_structuring/wells) ────────

export const wellOverviewDataAtom = atom<WellOverviewResponse | null>(null);
export const wellOverviewLoadingAtom = atom(false);
// Load-failure message. Non-null means the last fetch failed — the overview
// section shows an error + Retry instead of an empty table, so a 5xx/network
// failure is never silently indistinguishable from a doc with no staged wells.
export const wellOverviewErrorAtom = atom<string | null>(null);

export const fetchWellOverviewAtom = atom(
  null,
  async (_get, set, href: string | undefined) => {
    if (!href) return;
    set(wellOverviewLoadingAtom, true);
    set(wellOverviewErrorAtom, null);
    try {
      const response = await request.get<WellOverviewResponse>(href);
      if ('error' in response) {
        console.error('Failed to fetch AFE well overview:', response.error);
        set(wellOverviewErrorAtom, 'Unable to load wells.');
      } else {
        set(wellOverviewDataAtom, response);
      }
    } catch (error) {
      console.error('Failed to fetch AFE well overview:', error);
      set(wellOverviewErrorAtom, 'Unable to load wells.');
    } finally {
      set(wellOverviewLoadingAtom, false);
    }
  }
);

export const wellOverviewUrl = (documentId: number | string) =>
  `/documents/${documentId}/afe_structuring/wells`;

// ── Match Review detail (GET .../afe_structuring/wells/:id) ─────────────────

export const createWellDetailResourceAtom = (href: string | undefined) =>
  createResourceAtom<MatchReviewResponse>(href);

export const wellDetailUrl = (
  documentId: number | string,
  wellId: number | string
) => `/documents/${documentId}/afe_structuring/wells/${wellId}`;

// ── Lifecycle mutations ─────────────────────────────────────────────────────
//
// Each mutation atom takes a single argument describing the call. The setter
// returns the request Response so callers can branch on success/error.

/** Confirm a chosen suggestion (POST resources.confirmation.href). */
export const confirmMatchAtom =
  buildSectionMutationAtom<WellLifecycleResponse>();

/** Revert a confirmation (DELETE resources.confirmation.href). */
export const revertMatchAtom =
  buildSectionMutationAtom<WellLifecycleResponse>();

/** Change Match — pick existing / create generic (POST resources.matchCandidate.href). */
export const changeMatchAtom =
  buildSectionMutationAtom<WellLifecycleResponse>();

/** Rerun the well-matching agent (POST resources.suggestionRerun.href). */
export const rerunSuggestionsAtom =
  buildSectionMutationAtom<WellLifecycleResponse>();

/** Add a well — existing or generic (POST .../afe_structuring/wells). */
export const addWellAtom = buildSectionMutationAtom<WellLifecycleResponse>();

/** Edit a staged well (PATCH .../afe_structuring/wells/:id). */
export const editWellAtom = buildSectionMutationAtom<WellLifecycleResponse>();

/** Remove a well (DELETE .../afe_structuring/wells/:id). */
export const removeWellAtom = buildSectionMutationAtom<WellLifecycleResponse>();

/** Restore a removed well (POST .../afe_structuring/wells/:id/restoration). */
export const restoreWellAtom =
  buildSectionMutationAtom<WellLifecycleResponse>();
